import React, { useContext, useEffect, useState } from "react";
import { secured } from "react-abac";
import { Tree, TreeNode } from "react-organizational-chart";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Space } from "react-zoomable-ui";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Alert, Grid, Section, Text } from "~/components-shared";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CoreContext from "~/lib/context";
import OrgChartMemberCard from "~/components/OrgChartMemberCard";
import OrgChartCompanyCard from "~/components/OrgChartCompanyCard";
import styled, { useTheme } from "styled-components";
import memberTreeActionCreators from "~/store/memberTree/actions";
import memberTreeSelectors from "~/store/memberTree/selectors";
import doodles from "~/assets/i/patterns/doodles.jpg";
import InputMemberSelect from "~/components/InputMemberSelect";
import useAbacExt from "~/hooks/abac";
const StyledSection = styled(Section)`
  display: flex;
  flex-direction: column;
  height: 100vh;
`;
const Pane = styled.div`
  flex-grow: 1;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.secondary;
}};
  border-radius: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.shape.borderRadius.primary;
}};
  display: flex;
  overflow: hidden;

  &:before {
    content: "";
    background: url(${doodles});
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0.02;
  }
`;
const RecursiveNodes = _ref3 => {
  let {
    items
  } = _ref3;
  return /*#__PURE__*/React.createElement(React.Fragment, null, items.map(item => /*#__PURE__*/React.createElement(TreeNode, {
    key: item.id,
    label: /*#__PURE__*/React.createElement(OrgChartMemberCard, {
      treeItem: item
    })
  }, item.children && /*#__PURE__*/React.createElement(RecursiveNodes, {
    items: item.children
  }))));
};
const OrgChart = () => {
  const theme = useTheme();
  const recursiveTree = useSelector(memberTreeSelectors.getRecursiveMemberTree);
  const memberTree = useSelector(memberTreeSelectors.getMemberTree);
  const status = useSelector(memberTreeSelectors.getStatus);
  const error = useSelector(memberTreeSelectors.getError);
  const [selectedMember, setSelectedMember] = useState("");
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    userHas
  } = useAbacExt();
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(memberTreeActionCreators.fetchStarted());
    }
  }, [status, dispatch]);
  const handleAddMember = memberId => {
    setSelectedMember("");
    if (memberId) {
      dispatch(memberTreeActionCreators.createStarted(memberId));
    }
  };
  return /*#__PURE__*/React.createElement(StyledSection, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mt: 5,
      mb: 8
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("OrgChart.orgChart")), userHas(AuthoritiesEnum.ORG_CHART_WRITE) ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr 300px",
    gap: 5,
    s: {
      mb: 5
    },
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, /*#__PURE__*/React.createElement(Text, {
    element: "p",
    fontSize: 5,
    lineHeight: 7,
    color: "textSecondary"
  }, t("OrgChart.descriptionUpdate")), /*#__PURE__*/React.createElement(InputMemberSelect, {
    value: selectedMember,
    placeholder: t("OrgChart.addMember"),
    onChange: handleAddMember,
    filterMembers: memberTree?.map(_ref4 => {
      let {
        member
      } = _ref4;
      return member.id;
    }),
    loading: status === StatusStateEnum.LOADING,
    rounded: true
  })) : /*#__PURE__*/React.createElement(Text, {
    element: "p",
    fontSize: 5,
    lineHeight: 7,
    color: "textSecondary",
    s: {
      mb: 5
    },
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("OrgChart.descriptionRead")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Pane, {
    id: "elemento"
  }, /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement(Space, {
    style: {
      cursor: "grab"
    },
    onCreate: vp => {
      vp.setBounds({
        zoom: [0.2, 2]
      });
    }
  }, /*#__PURE__*/React.createElement(Tree, {
    lineWidth: "2px",
    lineColor: theme.colors.borders.primary,
    lineBorderRadius: "12px",
    label: /*#__PURE__*/React.createElement(OrgChartCompanyCard, {
      memberCount: recursiveTree.length
    })
  }, /*#__PURE__*/React.createElement(RecursiveNodes, {
    items: recursiveTree
  }))))));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.ORG_CHART_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(OrgChart), () => true);